import {defer, MonoTypeOperatorFunction, ReplaySubject} from 'rxjs';
import {filter, finalize, takeUntil} from 'rxjs/operators';

/** Owns only the reads started by one pull, never unrelated screen work. */
export class PullToRefreshSession {
  private static nextId = 0;
  readonly id = ++PullToRefreshSession.nextId;
  private readonly stopped = new ReplaySubject<void>(1);
  readonly cancelled$ = this.stopped.asObservable();
  private rollbacks: Array<() => void> = [];
  private cleanups: Array<() => void> = [];
  private pending = 0;
  active = true;
  cancelled = false;

  constructor(private readonly finished: () => void = () => undefined) {}

  /** Put this at the end of the complete request chain, after flattening operators. */
  operator<T>(): MonoTypeOperatorFunction<T> {
    return source => defer(() => {
      this.pending++;
      return source.pipe(
        takeUntil(this.cancelled$),
        filter(() => this.active),
        finalize(() => {
          this.pending--;
          if (!this.pending) this.finish();
        }),
      );
    });
  }

  /** Loading flags must also be released when a reused screen changes context. */
  onStop(cleanup: () => void): void {
    if (this.active) this.cleanups.push(cleanup);
  }

  private cleanup(): void {
    const callbacks = this.cleanups;
    this.cleanups = [];
    callbacks.forEach(callback => callback());
  }

  onCancel(restore: () => void): void {
    if (this.active) this.rollbacks.push(restore);
  }

  /** Retain references to the last committed data; request code replaces these fields. */
  capture<T extends object, K extends keyof T>(target: T, keys: K[]): void {
    const values = keys.map(key => target[key]);
    this.onCancel(() => keys.forEach((key, index) => target[key] = values[index]));
  }

  complete(): void {
    if (!this.pending) this.finish();
  }

  cancel(restore = true): void {
    if (!this.active) return;
    this.active = false;
    this.cancelled = true;
    // Mark inactive before unsubscribing: finalize/complete callbacks cannot commit.
    this.stopped.next();
    this.stopped.complete();
    this.cleanup();
    if (restore) this.rollbacks.forEach(rollback => rollback());
    this.rollbacks = [];
    this.finished();
  }

  private finish(): void {
    if (!this.active) return;
    this.active = false;
    this.rollbacks = [];
    this.stopped.next();
    this.stopped.complete();
    this.cleanup();
    this.finished();
  }
}

export function duringRefresh<T>(session?: PullToRefreshSession): MonoTypeOperatorFunction<T> {
  return session ? session.operator<T>() : source => source;
}
